import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Helper to extract a consistent error message
const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.data?.message) return error.data.message;
  return 'An error occurred';
};

// Fetch business profile for a specific phone number
export const fetchBusinessProfile = createAsyncThunk(
  'businessProfiles/fetch',
  async ({ businessAccountId, phoneNumberId, sync = false }, { rejectWithValue }) => {
    try {
      const response = await apiService.businessProfiles.get({
        businessAccountId,
        phoneNumberId,
        sync,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error.response?.data || error));
    }
  }
);

// Update / save business profile
export const updateBusinessProfile = createAsyncThunk(
  'businessProfiles/update',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.businessProfiles.update(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error.response?.data || error));
    }
  }
);

// Sync profile from Meta
export const syncBusinessProfile = createAsyncThunk(
  'businessProfiles/sync',
  async ({ businessAccountId, phoneNumberId }, { rejectWithValue }) => {
    try {
      const response = await apiService.businessProfiles.sync({
        businessAccountId,
        phoneNumberId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error.response?.data || error));
    }
  }
);

const initialState = {
  profile: null,
  loading: false,
  saving: false,
  syncing: false,
  error: null,
};

const businessProfilesSlice = createSlice({
  name: 'businessProfiles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearProfile: (state) => {
      state.profile = null;
    },
  },
  extraReducers: (builder) => {
    // fetchBusinessProfile
    builder
      .addCase(fetchBusinessProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.data || action.payload;
      })
      .addCase(fetchBusinessProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // updateBusinessProfile
    builder
      .addCase(updateBusinessProfile.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateBusinessProfile.fulfilled, (state, action) => {
        state.saving = false;
        state.profile = action.payload.data || action.payload;
      })
      .addCase(updateBusinessProfile.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });

    // syncBusinessProfile
    builder
      .addCase(syncBusinessProfile.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(syncBusinessProfile.fulfilled, (state, action) => {
        state.syncing = false;
        state.profile = action.payload.data || action.payload;
      })
      .addCase(syncBusinessProfile.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearProfile } = businessProfilesSlice.actions;
export default businessProfilesSlice.reducer;
